import { computeCoachingConfidence, defaultCoachAdvice } from './coachAdvice';
import { clamp01, normalizeRange, titleCase, formatFeet, formatSignedMph } from './helpers';

export function classifyBrakingIssue({
  corner,
  referenceCorner = null,
}) {
  const peakSpreadThreshold = corner?.peakUnit === 'G' ? 0.18 : 14;
  const startSpread = corner?.brakeStartSpreadFt ?? 0;
  const trailSpread = corner?.trailBrakeSpreadFt ?? 0;
  const peakSpread = corner?.peakBrakeSpread ?? 0;
  const delta = corner?.best3Delta || null;
  const brakeStartDelta = delta?.brakeStartDeltaFt ?? null;
  const trailDelta = delta?.trailBrakeDeltaFt ?? null;
  const minSpeedDelta = delta?.minSpeedDeltaMph ?? null;
  const exitSpeedDelta = delta?.exitSpeedDeltaMph ?? null;
  const eventCount = corner?.eventCount ?? 0;
  const longGNoise = corner?.sourceUsed === 'longG' ? 0.18 : 0.05;
  const smallSampleNoise = eventCount < 4 ? 0.18 : eventCount < 6 ? 0.08 : 0;

  if (eventCount < 2) {
    return {
      type: 'inconclusive',
      patternStrength: 0.1,
      outcomeCorrelation: 0,
      causalClarity: 0.1,
      signalNoise: 0.5,
      referenceCorner,
    };
  }

  if (startSpread >= 20) {
    return {
      type: 'reference_inconsistent',
      patternStrength: normalizeRange(startSpread, 12, 30),
      outcomeCorrelation: clamp01(((minSpeedDelta != null && minSpeedDelta < 0 ? Math.abs(minSpeedDelta) : 0) / 2.5) + 0.35),
      causalClarity: 0.9,
      signalNoise: longGNoise + smallSampleNoise,
      referenceCorner,
    };
  }

  if (trailSpread >= 18 && startSpread < 12) {
    return {
      type: 'release_inconsistent',
      patternStrength: normalizeRange(trailSpread, 12, 28),
      outcomeCorrelation: clamp01(((minSpeedDelta != null && minSpeedDelta < 0 ? Math.abs(minSpeedDelta) : 0) / 2.8) + 0.25),
      causalClarity: 0.85,
      signalNoise: longGNoise + smallSampleNoise,
      referenceCorner,
    };
  }

  if (brakeStartDelta != null && brakeStartDelta > 12 && minSpeedDelta != null && minSpeedDelta < -1) {
    return {
      type: 'over_braking',
      patternStrength: clamp01((normalizeRange(brakeStartDelta, 10, 28) + normalizeRange(Math.abs(minSpeedDelta), 1, 3)) / 2),
      outcomeCorrelation: clamp01((normalizeRange(Math.abs(minSpeedDelta), 1, 3) + normalizeRange(brakeStartDelta, 10, 24)) / 2),
      causalClarity: 0.75,
      signalNoise: longGNoise + smallSampleNoise,
      referenceCorner,
    };
  }

  if (brakeStartDelta != null && brakeStartDelta < -10 && ((minSpeedDelta != null && minSpeedDelta < -0.5) || (exitSpeedDelta != null && exitSpeedDelta < -0.5))) {
    return {
      type: 'too_aggressive',
      patternStrength: clamp01((normalizeRange(Math.abs(brakeStartDelta), 10, 24) + normalizeRange(Math.abs(Math.min(minSpeedDelta || 0, exitSpeedDelta || 0)), 0.5, 2.5)) / 2),
      outcomeCorrelation: clamp01((normalizeRange(Math.abs(Math.min(minSpeedDelta || 0, exitSpeedDelta || 0)), 0.5, 2.5) * 0.7) + 0.15),
      causalClarity: 0.68,
      signalNoise: longGNoise + smallSampleNoise + 0.05,
      referenceCorner,
    };
  }

  if (peakSpread >= peakSpreadThreshold) {
    return {
      type: 'application_inconsistent',
      patternStrength: normalizeRange(peakSpread, peakSpreadThreshold * 0.6, peakSpreadThreshold * 1.8),
      outcomeCorrelation: clamp01((minSpeedDelta != null && minSpeedDelta < 0 ? Math.abs(minSpeedDelta) / 3 : 0) + 0.18),
      causalClarity: 0.68,
      signalNoise: longGNoise + smallSampleNoise + 0.06,
      referenceCorner,
    };
  }

  if ((corner?.score ?? 0) >= 88) {
    return {
      type: 'stable',
      patternStrength: normalizeRange(corner.score, 88, 98),
      outcomeCorrelation: 0.45,
      causalClarity: 0.72,
      signalNoise: longGNoise,
      referenceCorner,
    };
  }

  if (trailDelta != null && trailDelta > 12) {
    return {
      type: 'holding_brake_too_long',
      patternStrength: normalizeRange(trailDelta, 8, 24),
      outcomeCorrelation: clamp01((minSpeedDelta != null && minSpeedDelta < 0 ? Math.abs(minSpeedDelta) / 2.8 : 0) + 0.18),
      causalClarity: 0.62,
      signalNoise: longGNoise + smallSampleNoise + 0.08,
      referenceCorner,
    };
  }

  return {
    type: 'mild_variation',
    patternStrength: 0.35,
    outcomeCorrelation: 0.2,
    causalClarity: 0.45,
    signalNoise: longGNoise + smallSampleNoise,
    referenceCorner,
  };
}

function buildObservation(corner, diagnosis, referenceLabel, user) {
  const delta = corner?.best3Delta || null;
  const eventNote = corner?.eventCount ? ` across ${corner.eventCount} braking events` : '';
  const ff = (v) => formatFeet(v, user);
  const fm = (v) => formatSignedMph(v, user);

  switch (diagnosis.type) {
    case 'reference_inconsistent':
      return `Brake start spread is ${ff(corner.brakeStartSpreadFt)} in ${corner.label}${eventNote}. That means your initial brake application point is shifting by roughly ${ff(corner.brakeStartSpreadFt)} from your earliest to latest braking lap. Trail brake spread is ${ff(corner.trailBrakeSpreadFt)} and peak brake spread is ${corner.peakUnit === 'G' ? `${(corner.peakBrakeSpread || 0).toFixed(2)} G` : `${Math.round(corner.peakBrakeSpread || 0)} ${corner.peakUnit || ''}`}.`;
    case 'release_inconsistent':
      return `Trail brake release spread is ${ff(corner.trailBrakeSpreadFt)} in ${corner.label}, while brake start spread is only ${ff(corner.brakeStartSpreadFt)}${eventNote}. You are hitting the brake in roughly the same place each lap, but releasing it at different points. ${delta?.minSpeedDeltaMph != null ? `This release inconsistency is associated with a ${fm(delta.minSpeedDeltaMph)} minimum-speed gap versus your ${referenceLabel.toLowerCase()}.` : ''}`;
    case 'over_braking':
      return `Versus your ${referenceLabel.toLowerCase()}, you brake ${ff(delta?.brakeStartDeltaFt)} earlier in ${corner.label} and give up ${fm(delta?.minSpeedDeltaMph)} at minimum speed${eventNote}. ${delta?.exitSpeedDeltaMph != null && delta.exitSpeedDeltaMph < -0.3 ? `Exit speed is also down by ${fm(delta.exitSpeedDeltaMph)}, which carries onto the following straight.` : 'Exit speed is not significantly affected yet, but the earlier braking is still scrubbing unnecessary speed.'} Brake start spread is ${ff(corner.brakeStartSpreadFt)}.`;
    case 'too_aggressive':
      return `Versus your ${referenceLabel.toLowerCase()}, you brake ${ff(delta?.brakeStartDeltaFt)} later in ${corner.label} and lose ${fm(delta?.minSpeedDeltaMph)} at minimum speed${eventNote}. ${delta?.exitSpeedDeltaMph != null && delta.exitSpeedDeltaMph < -0.3 ? `Exit speed is also ${fm(delta.exitSpeedDeltaMph)} below your best, compounding the time loss onto the next straight.` : ''} The late brake application is pushing the car past the point where it can rotate cleanly.`;
    case 'application_inconsistent':
      return `${corner.peakLabel} spread is ${corner.peakUnit === 'G' ? `${corner.peakBrakeSpread.toFixed(2)} G` : `${Math.round(corner.peakBrakeSpread)} ${corner.peakUnit}`} in ${corner.label}${eventNote}, even though brake start timing is more stable at ${ff(corner.brakeStartSpreadFt)} spread. You are hitting the pedal at the same point but applying different amounts of pressure each lap, which changes how quickly the car decelerates.`;
    case 'holding_brake_too_long':
      return `You stay on the brake ${ff(delta?.trailBrakeDeltaFt)} longer than your ${referenceLabel.toLowerCase()} in ${corner.label}${eventNote}. Brake start spread is ${ff(corner.brakeStartSpreadFt)}, so the initial application point is consistent — the issue is how long you hold the pedal.${delta?.minSpeedDeltaMph != null && delta.minSpeedDeltaMph < -0.3 ? ` Minimum speed is ${fm(delta.minSpeedDeltaMph)} below your best, suggesting the extended braking is over-slowing the car before turn-in.` : ''}`;
    case 'stable':
      return `${corner.label} stays within ${ff(corner.brakeStartSpreadFt)} at brake start, ${ff(corner.trailBrakeSpreadFt)} at trail brake release, and carries stable corner speed${eventNote}. ${corner.score != null ? `Consistency score: ${corner.score}/100.` : ''} This is a clean, repeatable braking zone.`;
    case 'inconclusive':
      return `Only ${corner.eventCount || 'a few'} clean braking events recorded in ${corner.label}. There is not enough data to identify a reliable pattern or make a confident diagnosis yet.`;
    default:
      return `${corner.label} shows some braking variation${eventNote} — brake start spread is ${ff(corner.brakeStartSpreadFt)}, trail brake spread is ${ff(corner.trailBrakeSpreadFt)} — but no single issue dominates the pattern yet.`;
  }
}

function buildInference(diagnosis) {
  switch (diagnosis.type) {
    case 'reference_inconsistent':
      return 'Your braking reference is not locked in — you are picking a different brake point each lap. Without a consistent starting point, everything downstream (trail brake, minimum speed, exit) becomes unpredictable. This is typically the single biggest source of lap-time variation in a braking zone and should be addressed before tuning anything else.';
    case 'release_inconsistent':
      return 'The brake application is consistent, so your reference marker is working. The unstable phase is brake release — the transition from braking to cornering. Inconsistent release changes how much load is on the front tires at turn-in, which directly affects how the car rotates and how much minimum speed you carry. This is often caused by uncertain vision at the apex or a lack of a deliberate release technique.';
    case 'over_braking':
      return 'You are braking earlier than needed, which scrubs off speed before the corner entry. The car arrives at the turn-in point slower than it needs to be, which lowers minimum speed and delays your throttle application on exit. The compounding effect is significant: losing speed at mid-corner also costs you speed down the entire following straight. The physics are clear — a shorter, more decisive brake zone at the right distance will carry more entry speed without asking anything extra from the tires.';
    case 'too_aggressive':
      return 'The brake point is too deep, which overloads the front tires at turn-in and forces the car to scrub speed through the corner. When you brake too late, you either lock a tire, miss the apex, or carry too much speed past the clipping point — all of which force a slower, more defensive exit. A slightly earlier brake point will let the car rotate on its own and set up a significantly faster exit.';
    case 'application_inconsistent':
      return 'Brake timing is more consistent than brake force, which means the marker is working but your pedal technique is changing lap to lap. Different peak brake pressures cause different deceleration rates, which moves the turn-in point even when the brake point stays the same. This often happens when the driver is unsure about how much brake the corner needs, or when they are reacting to the car rather than proactively applying a planned amount of pressure.';
    case 'holding_brake_too_long':
      return 'The brake start and peak pressure are in the right range, but you are holding the pedal too deep into the corner. Extended trailing keeps the front tires loaded longer than necessary, which delays the transition to cornering grip and pushes the car past the ideal rotation point. Shorter braking durations let the car settle and turn earlier, which opens up the exit and gets you to throttle sooner.';
    case 'stable':
      return 'This braking zone has tight, repeatable numbers — consistent brake point, release, and corner speed. A stable braking zone like this is valuable because it tells you your process is working here: your visual reference, pedal technique, and release timing are all calibrated. This is the standard to replicate in corners that are less consistent.';
    default:
      return 'The data shows braking variation, but the pattern does not clearly point to one root cause yet. This can happen when multiple small issues overlap — for example, a slight brake-point drift combined with mild release variation. More laps or a clearer run set will help isolate the dominant factor.';
  }
}

function buildRecommendation(diagnosis, corner, confidenceLabel, user) {
  return defaultCoachAdvice.getAdvice({
    domain: 'braking',
    diagnosisType: diagnosis.type,
    confidenceLabel,
    corner,
    user,
  });
}

function buildTitle(diagnosis, corner) {
  switch (diagnosis.type) {
    case 'reference_inconsistent':
      return `${corner.label} needs a clearer brake reference`;
    case 'release_inconsistent':
      return `${corner.label} breaks down at release`;
    case 'over_braking':
      return `${corner.label} is an over-braking corner`;
    case 'too_aggressive':
      return `${corner.label} is too late to repeat cleanly`;
    case 'application_inconsistent':
      return `${corner.label} is inconsistent at peak brake`;
    case 'holding_brake_too_long':
      return `${corner.label} stays on the brake too long`;
    case 'stable':
      return `${corner.label} is your benchmark corner`;
    case 'inconclusive':
      return `${corner.label} needs more data`;
    default:
      return `${corner.label} has mild braking variation`;
  }
}

function buildType(diagnosis) {
  if (diagnosis.type === 'stable') return 'positive';
  if (diagnosis.type === 'inconclusive') return 'neutral';
  return 'warning';
}

export function buildCornerCoaching({
  corner,
  referenceCorner = null,
  referenceLabel = 'Best 3 laps',
  user,
}) {
  const diagnosis = classifyBrakingIssue({ corner, referenceCorner });
  const confidence = computeCoachingConfidence({
    sourceUsed: corner?.sourceUsed,
    eventCount: corner?.eventCount ?? 0,
    patternStrength: diagnosis.patternStrength,
    outcomeCorrelation: diagnosis.outcomeCorrelation,
    causalClarity: diagnosis.causalClarity,
    signalNoise: diagnosis.signalNoise,
  });

  return {
    id: `${corner.cornerId}-${diagnosis.type}`,
    cornerId: corner.cornerId,
    title: buildTitle(diagnosis, corner),
    type: buildType(diagnosis),
    diagnosis: diagnosis.type,
    observation: confidence.score >= 35 ? buildObservation(corner, diagnosis, referenceLabel, user) : null,
    inference: confidence.score >= 50 ? buildInference(diagnosis, corner) : null,
    recommendation: confidence.score >= 35 ? buildRecommendation(diagnosis, corner, confidence.label, user) : null,
    confidence,
    priority: Math.round((diagnosis.patternStrength * 100) + (confidence.score * 0.6)),
    meta: `${titleCase(confidence.label)} confidence`,
    referenceLabel,
  };
}

function buildPositiveFallback(coaching, actionableIds, referenceLabel) {
  const candidate = coaching
    .filter((card) => !actionableIds.has(card.id) && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];
  if (!candidate) return null;
  return {
    ...candidate,
    type: 'positive',
    title: `${candidate.cornerId ? candidate.cornerId : 'Best corner'} is your strongest braking zone`,
    diagnosis: 'best_available',
    recommendation: defaultCoachAdvice.getAdvice({
      domain: 'braking',
      diagnosisType: 'best_available',
      confidenceLabel: candidate.confidence.label,
      corner: { label: candidate.cornerId },
    }),
  };
}

export function buildBrakingCoachCards({
  analysis,
  referenceMode = 'best-3',
  user,
}) {
  if (!analysis?.corners?.length) return [];

  const referenceLabel = referenceMode === 'best-lap' ? 'Best lap' : 'Best 3 laps';
  const coaching = analysis.corners
    .map((corner) => buildCornerCoaching({
      corner,
      referenceCorner: referenceMode === 'best-lap' ? corner.bestLapReference : corner.best3Reference,
      referenceLabel,
      user,
    }))
    .filter((card) => card.confidence.score >= 35);

  const allActionable = coaching
    .filter((card) => card.diagnosis !== 'stable' && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.priority - a.priority);
  const actionable = allActionable.slice(0, 2);

  const benchmark = coaching
    .filter((card) => card.diagnosis === 'stable')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];

  const positiveCard = benchmark
    || buildPositiveFallback(coaching, new Set(actionable.map((c) => c.id)), referenceLabel);

  return [
    ...actionable,
    ...(positiveCard ? [positiveCard] : []),
  ];
}
